// pages/Auth/login.jsx - COMPLETELY FIXED WITH WORKING REDIRECT
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, EyeOff, LockKeyhole, Mail } from 'lucide-react';
import {
  loginUser,
  forgotPassword,
  resetPassword,
  clearMessages,
  selectAuthLoading,
  selectAuthError,
  selectAuthSuccessMessage,
  selectIsAuthenticated,
  selectForgotPasswordData,
  clearForgotPasswordData
} from '../../features/auth/loginSlice';
import './login.css';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const redirectExecuted = useRef(false);
  
  // Redux state
  const isLoading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const successMessage = useSelector(selectAuthSuccessMessage);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const forgotPasswordData = useSelector(selectForgotPasswordData);
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMobile, setResetMobile] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userType, setUserType] = useState('dealer');
  const [isOffice, setIsOffice] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  
  // Alert state
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [activePolicy, setActivePolicy] = useState(null);

  const policies = {
    terms: {
      title: 'Terms and Conditions',
      content: `Welcome to Alcobrake. By purchasing our products, you agree to the following terms and conditions:\n\n- Alcobrake sells Breathalyzers and Interlock devices exclusively through offline channels.\n- All sales are final. We do not offer returns, exchanges, or refunds.\n- Our products are intended for professional and legal use only. Alcobrake is not responsible for misuse or unauthorized modifications.\n- Prices and product availability are subject to change without prior notice.`
    },
    privacy: {
      title: 'Privacy Policy',
      content: `At Alcobrake, we value your privacy. This policy explains how we handle your personal information:\n\n- We collect only necessary customer details for offline transactions and support.\n- Your information is never shared, sold, or disclosed to third parties except as required by law.\n- We may use your contact details to provide support, product updates, and service-related communication.\n- We ensure reasonable security measures to protect your data from unauthorized access.\n- If you have any concerns about your privacy, please contact us at saireddy@alcobrake.com.`
    },
    shipping: {
      title: 'Shipping Policy',
      content: `Since Alcobrake operates solely through offline sales, the following shipping terms apply:\n\n- We do not process online orders or deliveries.\n- Products are directly handed over to customers upon purchase.\n- For bulk or special orders, delivery arrangements can be discussed with our sales team.\n- Customers are responsible for picking up their products from designated locations as agreed during purchase.`
    },
    contact: {
      title: 'Contact Us',
      content: `You may contact us using the information below:\n\nMerchant Legal entity name: Alcobrake Private Limited\nRegistered Address: First Floor of Ruby Tower, H. No. 198/9/3/4/17 & 18, Silicon Valley, Madhapur, Hi-tech City Hyderabad TELANGANA 500034\nOperational Address: Fifth Floor of Ruby Tower, H. No. 198/9/3/4/17 & 18, Silicon Valley, Madhapur, Hi-tech City Hyderabad TELANGANA 500034\nTelephone No: 8977285706\nE-Mail ID: saireddy@alcobrake.com`
    },
    refund: {
      title: 'Cancellation and Refund Policy',
      content: `- Alcobrake does not accept cancellations or provide refunds as all sales are made offline and finalized at the time of purchase.\n- We ensure thorough product demonstration and verification before finalizing any sale.\n- In case of defective or damaged products, please contact us immediately for assistance.`
    }
  };

  const openPolicy = (event, key) => {
    event.preventDefault();
    window.history.replaceState(null, '', `#${key}`);
    setActivePolicy(policies[key]);
  };

  const closePolicy = () => {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setActivePolicy(null);
  };

  const renderPolicyContent = (content) => content.split('saireddy@alcobrake.com').map((part, index, parts) => (
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 && <a href="mailto:saireddy@alcobrake.com">saireddy@alcobrake.com</a>}
    </React.Fragment>
  ));
  
  // Clear any stale auth state on component mount
  useEffect(() => {
    // Only clear if not already logged in
    const hasUser = 
      localStorage.getItem('admin_user') || 
      localStorage.getItem('fleetuser') ||
      localStorage.getItem('alcotrackerparentuser') ||
      localStorage.getItem('aargeeuser');
      
    if (!hasUser) {
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('loginTimestamp');
    }
    dispatch(clearMessages());
  }, [dispatch]);
  
  // ✅ FIXED: Simplified redirect with proper navigation
  useEffect(() => {
    if (sessionStorage.getItem('justLoggedOut') === 'true') {
      return;
    }

    const hasStoredSession = localStorage.getItem('isLoggedIn') === 'true' && (
      localStorage.getItem('admin_user') ||
      localStorage.getItem('fleetuser') ||
      localStorage.getItem('alcotrackerparentuser') ||
      localStorage.getItem('aargeeuser')
    );

    if (isAuthenticated && hasStoredSession && !redirectExecuted.current) {
      redirectExecuted.current = true;
      let storedUser = {};
      try {
        storedUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
      } catch (error) {
        storedUser = {};
      }
      const role = String(storedUser.role || storedUser.accesslevel || '').toLowerCase();
      navigate(role === 'office' ? '/clients' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);
  
  // Handle messages
  useEffect(() => {
    if (successMessage) {
      showAlert('success', successMessage);
      dispatch(clearMessages());
      
      if (successMessage.includes('Password updated')) {
        setTimeout(() => {
          setShowForgotPassword(false);
          resetForgotPasswordForm();
        }, 2000);
      }
    }
    
    if (error) {
      showAlert('error', error);
      dispatch(clearMessages());
    }
  }, [successMessage, error, dispatch]);
  
  // Handle forgot password data
  useEffect(() => {
    if (forgotPasswordData) {
      setShowResetForm(true);
      dispatch(clearForgotPasswordData());
    }
  }, [forgotPasswordData, dispatch]);
  
  // Load remembered email
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);
  
  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      showAlert('error', 'Please enter both email and password');
      return;
    }
    
    // Reset redirect flag for new login attempt
    redirectExecuted.current = false;
    
    try {
      const result = await dispatch(loginUser({ email, password })).unwrap();
      console.log('Login result:', result);
      sessionStorage.removeItem('justLoggedOut');
      
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      redirectExecuted.current = true;
      const role = String(result?.role || result?.accesslevel || '').toLowerCase();
      const loggedInEmail = String(result?.emailId || result?.email || '').trim().toLowerCase();
      navigate(
        loggedInEmail === 'alcobrakeproduction@gmail.com' || role === 'office'
          ? '/clients'
          : '/dashboard',
        { replace: true }
      );
    } catch (err) {
      console.error('Login error:', err);
      showAlert('error', err.message || 'Login failed');
    }
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail || !resetMobile) {
      showAlert('error', 'Please enter both email and mobile number');
      return;
    }
    
    try {
      await dispatch(forgotPassword({ 
        email: resetEmail, 
        mobile: resetMobile 
      })).unwrap();
    } catch (err) {
      console.error('Forgot password error:', err);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!resetEmail) {
      showAlert('error', 'Please enter your email address');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showAlert('error', 'Passwords do not match');
      return;
    }
    
    if (newPassword.length < 4) {
      showAlert('error', 'Password must be at least 4 characters');
      return;
    }
    
    try {
      await dispatch(resetPassword({
        email: resetEmail,
        newPassword: newPassword,
        confirmPassword: confirmPassword,
        currentPassword: password,
        type: userType
      })).unwrap();
      
      resetForgotPasswordForm();
    } catch (err) {
      console.error('Reset password error:', err);
    }
  };
  
  const resetForgotPasswordForm = () => {
    setResetEmail('');
    setResetMobile('');
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setUserType('dealer');
    setIsOffice(false);
    setShowResetForm(false);
    setShowForgotPassword(false);
  };
  
  const handleUserTypeChange = (checked) => {
    setIsOffice(checked);
    setUserType(checked ? 'office' : 'dealer');
  };

  const openRecoverAccount = () => {
    dispatch(clearMessages());
    setResetEmail(email);
    setShowResetForm(true);
    setShowForgotPassword(true);
  };
  
  return (
    <div className="login-container">
      {alert.show && (
        <div className={`alert-banner ${alert.type}`}>
          <span>{alert.message}</span>
          <button className="alert-close" onClick={() => setAlert({ show: false })}>✕</button>
        </div>
      )}
      
      <div className="login-shell">
        <aside className="login-brand-panel">
          <div className="login-brand-copy">
            {/* <div className="login-brand-logo" aria-label="AlcoBrake"><span>Alco</span><b>Brake</b></div> */}
            <h1>Welcome Back</h1>
            <p>Manage your ignition interlock operations in one place.</p>
          </div>
        </aside>

      <div className="login-card">
        <div className="login-header">
          {/* <div className="logo-container">
            <img src="/alcobrake1.jpg" alt="AlcoBrake" className="logo" />
          </div> */}
          <h2>{showForgotPassword ? (showResetForm ? 'Create new password' : 'Recover your account') : 'Login to Account'}</h2>
          <p>{showForgotPassword ? 'Verify your account details to continue.' : 'Sign in with your authorized account.'}</p>
        </div>
        
        {!showForgotPassword ? (
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="login-input-with-icon"><Mail size={18} /><input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" disabled={isLoading} autoComplete="email" /></div>
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <LockKeyhole className="login-field-icon" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                </button>
              </div>
            </div>
            
            <div className="form-options">
              {/* <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                /> 
                Remember email
              </label> */}
              <button 
                type="button" 
                className="forgot-password"
                onClick={openRecoverAccount}
              >
                Forgot password?
              </button>
            </div>
            
            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in securely...' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={showResetForm ? handleResetPassword : handleForgotPassword} className="login-form">
            {!showResetForm ? (
              <>
                <div className="form-group">
                  <label htmlFor="reset-email">Email Address</label>
                  <input
                    type="email"
                    id="reset-email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Enter your email"
                    disabled={isLoading}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="reset-mobile">Mobile Number</label>
                  <input
                    type="text"
                    id="reset-mobile"
                    value={resetMobile}
                    onChange={(e) => setResetMobile(e.target.value)}
                    placeholder="Enter your mobile number"
                    disabled={isLoading}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={isOffice}
                      onChange={(e) => handleUserTypeChange(e.target.checked)}
                    />
                    <span>I am an Officer</span>
                  </label>
                </div>
                
                <button 
                  type="submit" 
                  className="login-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Verifying...' : 'Verify User'}
                </button>
              </>
            ) : (
              <>
                <div className="recovery-type-row">
                  <div><strong>Account type</strong>
                  <span>Select Officer only for officer accounts</span></div>
                  <label className="recovery-switch">
                    <input type="checkbox" checked={isOffice} onChange={(e) => handleUserTypeChange(e.target.checked)} />
                    <span className="recovery-switch-track"><i /></span>
                    <b>{isOffice ? 'Officer' : 'Dealer'}</b>
                  </label>
                </div>
                <div className="form-group">
                  <label htmlFor="recovery-email">Email Address</label>
                  <input
                    type="email"
                    id="recovery-email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Enter your account email"
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-password">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min. 4 characters)"
                      disabled={isLoading}
                      autoComplete="new-password"
                      required
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowNewPassword((visible) => !visible)} aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}>
                      {showNewPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      disabled={isLoading}
                      autoComplete="new-password"
                      required
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword((visible) => !visible)} aria-label={showConfirmPassword ? 'Hide confirmed password' : 'Show confirmed password'}>
                      {showConfirmPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                    </button>
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  className="login-button"
                  disabled={isLoading || newPassword !== confirmPassword || newPassword.length < 4}
                >
                  {isLoading ? 'Updating...' : 'Reset Password'}
                </button>
              </>
            )}
            
            <button 
              type="button" 
              className="back-to-login"
              onClick={resetForgotPasswordForm}
            >
              ← Back to Login
            </button>
          </form>
        )}
      </div>
      </div>
      {activePolicy && (
        <div className="policy-overlay" onClick={closePolicy} role="presentation">
          <section className="policy-dialog" role="dialog" aria-modal="true" aria-labelledby="policy-title" onClick={(event) => event.stopPropagation()}>
            <header>
              <h2 id="policy-title">{activePolicy.title}</h2>
              <button type="button" onClick={closePolicy} aria-label="Close policy">×</button>
            </header>
            <div className="policy-content">{renderPolicyContent(activePolicy.content)}</div>
            <footer><button type="button" onClick={closePolicy}>Close</button></footer>
          </section>
        </div>
      )}
      <footer className="login-page-footer">
        <nav><a href="#terms" onClick={(event) => openPolicy(event, 'terms')}>Terms and Conditions</a><a href="#privacy" onClick={(event) => openPolicy(event, 'privacy')}>Privacy Policy</a><a href="#shipping" onClick={(event) => openPolicy(event, 'shipping')}>Shipping Policy</a><a href="#contact" onClick={(event) => openPolicy(event, 'contact')}>Contact Us</a><a href="#refund" onClick={(event) => openPolicy(event, 'refund')}>Cancellation and Refund Policy</a></nav>
        <p>© {new Date().getFullYear()} Alcobrake Private Limited. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default Login;
